/**
 * ResetPassword.js
 * Set a new password using a reset token for the Procurement Intelligence Platform
 */

import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { API_BASE } from '../config';

const colors = {
  deepBlue: '#051C2C',
  electricBlue: '#2251FF',
  cyan: '#00A9F4',
  white: '#FFFFFF',
  lightGray: '#F5F7FA',
  mediumGray: '#E1E5EB',
  textMuted: '#8A9BAE',
  darkText: '#1A2B3C',
  danger: '#DC2626',
  success: '#059669',
};

function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [tokenError, setTokenError] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // Verify the token on page load
  useEffect(() => {
    if (!token) {
      setVerifying(false);
      setTokenError('No reset token provided. Please request a new password reset link.');
      return;
    }

    const verifyToken = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/auth/verify-reset-token`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token }),
        });

        const data = await res.json();

        if (data.valid) {
          setTokenValid(true);
        } else {
          setTokenError(data.reason || 'Invalid or expired reset link.');
        }
      } catch (err) {
        setTokenError('Unable to verify reset link. Please try again.');
      } finally {
        setVerifying(false);
      }
    };

    verifyToken();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);

    try {
      const res = await fetch(`${API_BASE}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, new_password: password }),
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || 'Failed to reset password.');
      }

      setSuccess(true);
    } catch (err) {
      setError(err.message || 'An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const renderContent = () => {
    // Loading state while verifying token
    if (verifying) {
      return (
        <div style={{ padding: '32px', textAlign: 'center' }}>
          <p style={{ color: colors.textMuted, fontSize: 'var(--font-size-base)' }}>
            Verifying your reset link...
          </p>
        </div>
      );
    }

    // Token is invalid or expired
    if (!tokenValid && !success) {
      return (
        <div style={{ padding: '32px' }}>
          <div style={{
            backgroundColor: `${colors.danger}15`,
            border: `1px solid ${colors.danger}30`,
            color: colors.danger,
            padding: '16px',
            borderRadius: '8px',
            marginBottom: '24px',
            fontSize: 'var(--font-size-base)',
            lineHeight: 1.6,
          }}>
            {tokenError}
          </div>
          <Link
            to="/forgot-password"
            style={{
              display: 'block',
              textAlign: 'center',
              width: '100%',
              padding: '12px',
              background: `linear-gradient(135deg, ${colors.electricBlue}, ${colors.cyan})`,
              color: colors.white,
              border: 'none',
              borderRadius: '12px',
              fontSize: 'var(--font-size-md)',
              fontWeight: 'var(--font-weight-semibold)',
              textDecoration: 'none',
              boxSizing: 'border-box',
            }}
          >
            Request New Reset Link
          </Link>
        </div>
      );
    }

    // Password was reset successfully
    if (success) {
      return (
        <div style={{ padding: '32px' }}>
          <div style={{
            backgroundColor: `${colors.success}15`,
            border: `1px solid ${colors.success}30`,
            color: colors.success,
            padding: '16px',
            borderRadius: '8px',
            marginBottom: '24px',
            fontSize: 'var(--font-size-base)',
            lineHeight: 1.6,
          }}>
            Password updated. You can now log in with your new password.
          </div>
          <Link
            to="/login"
            style={{
              display: 'block',
              textAlign: 'center',
              width: '100%',
              padding: '12px',
              background: `linear-gradient(135deg, ${colors.electricBlue}, ${colors.cyan})`,
              color: colors.white,
              border: 'none',
              borderRadius: '12px',
              fontSize: 'var(--font-size-md)',
              fontWeight: 'var(--font-weight-semibold)',
              textDecoration: 'none',
              boxSizing: 'border-box',
            }}
          >
            Go to Sign In
          </Link>
        </div>
      );
    }

    // Token is valid — show the password form
    return (
      <form onSubmit={handleSubmit} style={{ padding: '32px' }}>
        {error && (
          <div style={{
            backgroundColor: `${colors.danger}15`,
            border: `1px solid ${colors.danger}30`,
            color: colors.danger,
            padding: '12px 16px',
            borderRadius: '8px',
            marginBottom: '24px',
            fontSize: 'var(--font-size-base)',
          }}>
            {error}
          </div>
        )}

        <div style={{ marginBottom: '20px' }}>
          <label style={{
            display: 'block',
            marginBottom: '8px',
            fontWeight: 'var(--font-weight-medium)',
            fontSize: 'var(--font-size-base)',
            color: colors.darkText,
          }}>
            New Password
          </label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Minimum 8 characters"
            required
            disabled={loading}
            autoComplete="new-password"
            style={{
              width: '100%',
              padding: '12px 16px',
              border: `2px solid ${colors.mediumGray}`,
              borderRadius: '12px',
              fontSize: 'var(--font-size-base)',
              outline: 'none',
              transition: 'border-color 0.2s',
              boxSizing: 'border-box',
            }}
            onFocus={(e) => e.target.style.borderColor = colors.electricBlue}
            onBlur={(e) => e.target.style.borderColor = colors.mediumGray}
          />
        </div>

        <div style={{ marginBottom: '24px' }}>
          <label style={{
            display: 'block',
            marginBottom: '8px',
            fontWeight: 'var(--font-weight-medium)',
            fontSize: 'var(--font-size-base)',
            color: colors.darkText,
          }}>
            Confirm Password
          </label>
          <input
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder="Re-enter your new password"
            required
            disabled={loading}
            autoComplete="new-password"
            style={{
              width: '100%',
              padding: '12px 16px',
              border: `2px solid ${colors.mediumGray}`,
              borderRadius: '12px',
              fontSize: 'var(--font-size-base)',
              outline: 'none',
              transition: 'border-color 0.2s',
              boxSizing: 'border-box',
            }}
            onFocus={(e) => e.target.style.borderColor = colors.electricBlue}
            onBlur={(e) => e.target.style.borderColor = colors.mediumGray}
          />
        </div>

        <button
          type="submit"
          disabled={loading}
          style={{
            width: '100%',
            padding: '12px',
            background: `linear-gradient(135deg, ${colors.electricBlue}, ${colors.cyan})`,
            color: colors.white,
            border: 'none',
            borderRadius: '12px',
            fontSize: 'var(--font-size-md)',
            fontWeight: 'var(--font-weight-semibold)',
            cursor: loading ? 'not-allowed' : 'pointer',
            opacity: loading ? 0.7 : 1,
            transition: 'transform 0.2s, box-shadow 0.2s',
          }}
          onMouseEnter={(e) => {
            if (!loading) {
              e.target.style.transform = 'translateY(-1px)';
              e.target.style.boxShadow = `0 4px 12px ${colors.electricBlue}50`;
            }
          }}
          onMouseLeave={(e) => {
            e.target.style.transform = 'translateY(0)';
            e.target.style.boxShadow = 'none';
          }}
        >
          {loading ? 'Resetting...' : 'Reset Password'}
        </button>
      </form>
    );
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: `linear-gradient(135deg, ${colors.deepBlue} 0%, #0a2a4a 50%, ${colors.electricBlue}40 100%)`,
      padding: '20px',
    }}>
      <div style={{
        backgroundColor: colors.white,
        borderRadius: '16px',
        boxShadow: 'var(--shadow-lg)',
        width: '100%',
        maxWidth: '420px',
        overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{
          background: `linear-gradient(135deg, ${colors.electricBlue}, ${colors.cyan})`,
          padding: '32px',
          textAlign: 'center',
        }}>
          <div style={{
            width: '56px',
            height: '56px',
            borderRadius: '14px',
            backgroundColor: 'rgba(255,255,255,0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 16px',
          }}>
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4"/>
            </svg>
          </div>
          <h1 style={{
            color: colors.white,
            fontSize: 'var(--font-size-hero)',
            fontWeight: 'var(--font-weight-bold)',
            margin: '0 0 6px 0',
          }}>
            New Password
          </h1>
          <p style={{
            color: 'rgba(255,255,255,0.8)',
            fontSize: 'var(--font-size-base)',
            margin: 0,
          }}>
            Choose a new password for your account
          </p>
        </div>

        {renderContent()}

        {/* Footer */}
        <div style={{
          padding: '20px 32px',
          backgroundColor: colors.lightGray,
          borderTop: `1px solid ${colors.mediumGray}`,
        }}>
          <p style={{ margin: 0, fontSize: 'var(--font-size-sm)', color: colors.textMuted }}>
            Remember your password?{' '}
            <Link
              to="/login"
              style={{ color: colors.electricBlue, fontWeight: '500', textDecoration: 'none' }}
            >
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}

export default ResetPassword;
